import { useEffect, useState, useCallback } from 'react'
import { Save, Pencil, Trash2, Plus, Globe, FileText, ServerCog, Check } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  ProxyPreset, listProxyPresets, createProxyPreset,
  updateProxyPreset, deleteProxyPreset, Settings,
} from '../api'
import { confirm as appConfirm } from './ConfirmDialog'

type ProxyType = 'По ссылке' | 'Из файла' | 'Конкретное'

const TYPE_ICON: Record<string, React.ReactNode> = {
  'По ссылке': <Globe className="w-3.5 h-3.5" />,
  'Из файла': <FileText className="w-3.5 h-3.5" />,
  'Конкретное': <ServerCog className="w-3.5 h-3.5" />,
}

interface Props {
  cfg: Settings
  update: (patch: Partial<Settings>) => void
  onAlert?: (kind: 'success' | 'error', text: string) => void
}

// Determines whether the current PROXY_* fields contain anything worth saving.
function hasProxyDataToSave(cfg: Settings): boolean {
  const t = String(cfg['PROXY_TYPE'] ?? '')
  if (t === 'По ссылке') return Boolean(String(cfg['PROXY_MIND_LINK'] ?? '').trim())
  if (t === 'Из файла') return Boolean(String(cfg['PROXY_LIST'] ?? '').trim())
  if (t === 'Конкретное') return Boolean(String(cfg['PROXY_IPPORT'] ?? '').trim())
  return false
}

// Pick relevant fields from the project config so they can be stored as a preset.
function presetFromCfg(cfg: Settings): Partial<ProxyPreset> {
  return {
    type: String(cfg['PROXY_TYPE'] ?? 'По ссылке'),
    format: String(cfg['PROXY_FORMAT'] ?? 'HTTP(S)'),
    mind_link: String(cfg['PROXY_MIND_LINK'] ?? ''),
    proxy_list: String(cfg['PROXY_LIST'] ?? ''),
    ipport: String(cfg['PROXY_IPPORT'] ?? ''),
    shuffle: String(cfg['PROXY_SHUFFLE'] ?? '') === 'Да',
  }
}

// Apply a preset to the project: rewrite the PROXY_* fields in cfg.
function applyPresetToCfg(p: ProxyPreset, update: (patch: Partial<Settings>) => void) {
  update({
    PROXY_USE: 'Да',
    PROXY_TYPE: p.type,
    PROXY_FORMAT: p.format,
    PROXY_MIND_LINK: p.mind_link || '',
    PROXY_LIST: p.proxy_list || '',
    PROXY_IPPORT: p.ipport || '',
    PROXY_SHUFFLE: p.shuffle ? 'Да' : '',
  })
}

// Detect which preset (if any) the project's current proxy fields exactly match.
// Bug #173: секретные поля (PROXY_MIND_LINK, PROXY_LIST, PROXY_IPPORT) для
// не-admin пользователей приходят с сервера как "***" (masked). В этом случае
// проверку конкретного значения пропускаем — считаем что тип/формат совпадения
// достаточно для идентификации активного пресета. Иначе у operator-пользователей
// пресет всегда казался «неактивным» после загрузки настроек.
function activePresetId(cfg: Settings, presets: ProxyPreset[]): number | null {
  const t = String(cfg['PROXY_TYPE'] ?? '')
  const fmt = String(cfg['PROXY_FORMAT'] ?? '')
  const mind = String(cfg['PROXY_MIND_LINK'] ?? '')
  const lst = String(cfg['PROXY_LIST'] ?? '')
  const ip = String(cfg['PROXY_IPPORT'] ?? '')
  const shuf = String(cfg['PROXY_SHUFFLE'] ?? '') === 'Да'
  // '***' = masked sensitive field — сервер скрыл значение для не-admin.
  // Пропускаем проверку содержимого, матчим только по типу/формату.
  const isMasked = (v: string) => v === '***'
  for (const p of presets) {
    if (p.type !== t) continue
    if (p.format !== fmt) continue
    if (p.type === 'По ссылке' && !isMasked(mind) && p.mind_link !== mind) continue
    if (p.type === 'Из файла') {
      if (!isMasked(lst) && p.proxy_list !== lst) continue
      // shuffle — не секретное поле, всегда сравниваем
      if (p.shuffle !== shuf) continue
    }
    if (p.type === 'Конкретное' && !isMasked(ip) && p.ipport !== ip) continue
    return p.id
  }
  return null
}

export default function ProxyPresetsPanel({ cfg, update, onAlert }: Props) {
  const [presets, setPresets] = useState<ProxyPreset[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<ProxyPreset | null>(null)
  const [creating, setCreating] = useState<{ from: 'current' | 'blank' } | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const list = await listProxyPresets()
      setPresets(list)
    } catch {
      // ignore — user will see the empty list
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void reload() }, [reload])

  const activeId = activePresetId(cfg, presets)
  const visiblePresets = presets.filter(p => p.type !== 'Конкретное')

  const apply = async (p: ProxyPreset) => {
    if (activeId === p.id) {
      onAlert?.('success', `Пресет «${p.name}» уже применён`)
      return
    }
    applyPresetToCfg(p, update)
    onAlert?.('success', `Применён пресет «${p.name}»`)
  }

  const remove = async (p: ProxyPreset) => {
    if (!await appConfirm({
      title: `Удалить пресет «${p.name}»?`,
      description: 'Сам пресет будет удалён из общего списка. Настройки в проектах, где он применён, не изменятся.',
      confirmText: 'Удалить',
      destructive: true,
    })) return
    const r = await deleteProxyPreset(p.id)
    if (r.error) {
      onAlert?.('error', r.error)
      return
    }
    onAlert?.('success', `Пресет «${p.name}» удалён`)
    await reload()
  }

  return (
    <div className="rounded-xl border border-border bg-muted/20 px-4 py-3.5 mb-4">
      <div className="flex items-center justify-between mb-3 gap-3">
        <div>
          <div className="text-sm font-semibold text-foreground">Пресеты прокси</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Сохранённые конфигурации прокси для быстрого применения к любому проекту.
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setCreating({ from: 'blank' })}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Новый
          </Button>
          <Button
            variant="default"
            size="sm"
            className="h-8 text-xs"
            disabled={!hasProxyDataToSave(cfg)}
            onClick={() => setCreating({ from: 'current' })}
            title={hasProxyDataToSave(cfg) ? 'Сохранить текущие настройки прокси как пресет' : 'Сначала заполните настройки прокси'}
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            Сохранить текущие
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground italic px-2 py-3">Загрузка...</div>
      ) : visiblePresets.length === 0 ? (
        <div className="text-xs text-muted-foreground italic px-2 py-3">
          Пресетов пока нет. Заполните настройки прокси и нажмите «Сохранить текущие».
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {visiblePresets.map(p => {
            const isActive = p.id === activeId
            return (
              <div
                key={p.id}
                className={`group relative rounded-lg border px-3 py-2 transition-all cursor-pointer hover:border-primary/40 hover:bg-primary/5 ${
                  isActive
                    ? 'border-primary bg-primary/10 shadow-sm'
                    : 'border-border bg-background'
                }`}
                onClick={() => void apply(p)}
              >
                <div className="flex items-start gap-2.5 min-w-0">
                  <div className={`shrink-0 mt-0.5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                    {TYPE_ICON[p.type] ?? <Globe className="w-3.5 h-3.5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-foreground truncate">{p.name}</span>
                      {isActive && (
                        <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {p.type} · {p.format}
                      {p.type === 'По ссылке' && p.mind_link && (
                        <span className="ml-1 opacity-70">— {p.mind_link.slice(0, 40)}{p.mind_link.length > 40 ? '…' : ''}</span>
                      )}
                      {p.type === 'Из файла' && p.proxy_list && (
                        <span className="ml-1 opacity-70">— {p.proxy_list.split('\n').filter(Boolean).length} строк</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setEditing(p) }}
                      className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
                      title="Редактировать"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); void remove(p) }}
                      className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      title="Удалить"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {(creating || editing) && (
        <PresetEditDialog
          initial={editing ?? (creating?.from === 'current' ? presetFromCfg(cfg) : { type: 'По ссылке', format: 'HTTP(S)' })}
          isEdit={!!editing}
          onClose={() => { setEditing(null); setCreating(null) }}
          onSaved={async () => { setEditing(null); setCreating(null); await reload() }}
          onAlert={onAlert}
        />
      )}
    </div>
  )
}

interface PresetEditDialogProps {
  initial: Partial<ProxyPreset>
  isEdit: boolean
  onClose: () => void
  onSaved: () => Promise<void> | void
  onAlert?: (kind: 'success' | 'error', text: string) => void
}

function PresetEditDialog({ initial, isEdit, onClose, onSaved, onAlert }: PresetEditDialogProps) {
  const [name, setName] = useState(initial.name ?? '')
  const [type, setType] = useState<ProxyType>((initial.type as ProxyType) ?? 'По ссылке')
  const [format, setFormat] = useState(initial.format ?? 'HTTP(S)')
  const [mindLink, setMindLink] = useState(initial.mind_link ?? '')
  const [proxyList, setProxyList] = useState(initial.proxy_list ?? '')
  const [shuffle, setShuffle] = useState(Boolean(initial.shuffle))
  const [saving, setSaving] = useState(false)

  // The "Конкретное" type is intentionally not exposed in the preset
  // editor — single-IP proxies are project-specific by nature; presets
  // make sense for shared "По ссылке"/"Из файла" configurations.

  const save = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      onAlert?.('error', 'Укажите имя пресета')
      return
    }
    setSaving(true)
    try {
      const payload: Partial<ProxyPreset> = {
        name: trimmed,
        type,
        format,
        mind_link: type === 'По ссылке' ? mindLink : '',
        proxy_list: type === 'Из файла' ? proxyList : '',
        ipport: '',
        shuffle: type === 'Из файла' ? shuffle : false,
      }
      const r = isEdit && initial.id != null
        ? await updateProxyPreset(initial.id, payload)
        : await createProxyPreset(payload)
      if (r.error) {
        onAlert?.('error', r.error)
        return
      }
      onAlert?.('success', isEdit ? `Пресет «${trimmed}» обновлён` : `Пресет «${trimmed}» создан`)
      await onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Редактирование пресета' : 'Новый пресет прокси'}</DialogTitle>
          <DialogDescription>
            Пресет можно применить к любому проекту одним кликом.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="preset-name" className="text-xs">Название пресета</Label>
            <Input
              id="preset-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: «Mobile Proxy RU»"
              className="text-sm"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Тип</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {(['По ссылке', 'Из файла'] as ProxyType[]).map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setType(opt)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-all ${
                    type === opt
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/30 hover:text-foreground'
                  }`}
                >
                  {TYPE_ICON[opt]}
                  <span>{opt}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Формат</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {['HTTP(S)', 'SOCKS5'].map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setFormat(opt)}
                  className={`px-3 py-2 rounded-md border text-sm transition-all ${
                    format === opt
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/30 hover:text-foreground'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {type === 'По ссылке' && (
            <div className="space-y-1.5">
              <Label htmlFor="preset-link" className="text-xs">Ссылка на получение прокси</Label>
              <Input
                id="preset-link"
                value={mindLink}
                onChange={(e) => setMindLink(e.target.value)}
                placeholder="https://your-api/proxy"
                className="text-sm"
              />
            </div>
          )}

          {type === 'Из файла' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="preset-list" className="text-xs">Список прокси (по одному на строку)</Label>
                <Textarea
                  id="preset-list"
                  value={proxyList}
                  onChange={(e) => setProxyList(e.target.value)}
                  placeholder="ip:port:user:pass&#10;ip:port:user:pass"
                  rows={5}
                  className="text-sm font-mono"
                />
                <div className="text-[11px] text-muted-foreground">
                  {proxyList.split('\n').filter(Boolean).length} строк
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={shuffle}
                  onChange={(e) => setShuffle(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-input"
                />
                <span>Перемешать прокси при загрузке</span>
              </label>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Отмена</Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? 'Сохранение…' : (isEdit ? 'Сохранить' : 'Создать')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
